import {
  AccountsUnsupportedError,
  EmailTakenError,
  InsecureServerUrlError,
  ForeignLocalDataCancelledError,
  InvalidCredentialsError,
  InvalidRecoveryCodeError,
  KdfParamsRejectedError,
  PassphraseMismatchError,
  PassphraseSpacesDisabledError,
  RegistrationClosedError,
  RegistrationNotConfirmedError,
  SessionRejectedError,
  SpaceTakenError,
  InvalidPassphraseError,
  InvalidServerUrlError,
  ServerErrorCode,
  SyncAlreadyEnabledError,
  SyncHttpError,
  SyncNetworkError,
  SyncNotEnabledError,
  type SyncErrorCode,
} from '@wiztime/sync';

/** Translation keys of the messages shown when enabling or joining sync fails. */
export const SyncFailureKey = {
  InvalidPassphrase: 'sync.fail.invalidPassphrase',
  InvalidServer: 'sync.fail.invalidServer',
  InsecureServer: 'sync.fail.insecureServer',
  AlreadyEnabled: 'sync.fail.alreadyEnabled',
  NotEnabled: 'sync.fail.notEnabled',
  ForeignDataCancelled: 'sync.fail.foreignDataCancelled',
  Network: 'sync.fail.network',
  Mismatch: 'sync.error.forbidden',
  RateLimited: 'sync.error.rate_limited',
  QuotaExceeded: 'sync.error.quota_exceeded',
  ServerUnavailable: 'sync.error.service_unavailable',
  Server: 'sync.fail.server',
  Unknown: 'sync.fail.unknown',
} as const;
export type SyncFailureKey = (typeof SyncFailureKey)[keyof typeof SyncFailureKey];

/** The message for an error thrown by `enableSync`, `joinSync` or `deleteRemoteData`. */
export function syncFailureKey(error: unknown): SyncFailureKey {
  if (error instanceof InvalidPassphraseError) return SyncFailureKey.InvalidPassphrase;
  // The insecure-address error is a kind of invalid address: test it first.
  if (error instanceof InsecureServerUrlError) return SyncFailureKey.InsecureServer;
  if (error instanceof InvalidServerUrlError) return SyncFailureKey.InvalidServer;
  if (error instanceof SyncAlreadyEnabledError) return SyncFailureKey.AlreadyEnabled;
  if (error instanceof SyncNotEnabledError) return SyncFailureKey.NotEnabled;
  if (error instanceof ForeignLocalDataCancelledError) return SyncFailureKey.ForeignDataCancelled;
  if (error instanceof SyncNetworkError) return SyncFailureKey.Network;
  if (error instanceof SyncHttpError) {
    if (error.code === ServerErrorCode.Forbidden || error.code === ServerErrorCode.Unauthorized)
      return SyncFailureKey.Mismatch;
    if (error.code === ServerErrorCode.RateLimited) return SyncFailureKey.RateLimited;
    if (error.code === ServerErrorCode.QuotaExceeded) return SyncFailureKey.QuotaExceeded;
    if (error.code === ServerErrorCode.ServiceUnavailable) return SyncFailureKey.ServerUnavailable;
    return SyncFailureKey.Server;
  }
  return SyncFailureKey.Unknown;
}

/** Keys of the translated message of each status error code (every code has one). */
export function statusErrorKey(code: SyncErrorCode): `sync.error.${SyncErrorCode}` {
  return `sync.error.${code}`;
}

/** Translation keys of the messages shown when an account operation fails. */
export const AccountFailureKey = {
  EmailTaken: 'sync.account.fail.emailTaken',
  InvalidCredentials: 'sync.account.fail.invalidCredentials',
  InvalidRecoveryCode: 'sync.account.fail.invalidRecoveryCode',
  RegistrationClosed: 'sync.account.fail.registrationClosed',
  RegistrationNotConfirmed: 'sync.account.fail.registrationNotConfirmed',
  KdfRejected: 'sync.account.fail.kdfRejected',
  PassphraseMismatch: 'sync.account.fail.passphraseMismatch',
  SpaceTaken: 'sync.account.fail.spaceTaken',
  PassphraseSpacesDisabled: 'sync.account.fail.passphraseSpacesDisabled',
  AccountsUnsupported: 'sync.account.fail.accountsUnsupported',
  SessionRejected: 'sync.account.fail.sessionRejected',
  TooManyMails: 'sync.account.fail.tooManyMails',
} as const;
export type AccountFailureKey = (typeof AccountFailureKey)[keyof typeof AccountFailureKey];

/** The message for an error thrown by an account operation; other errors use the sync messages. */
export function accountFailureKey(error: unknown): AccountFailureKey | SyncFailureKey {
  if (error instanceof EmailTakenError) return AccountFailureKey.EmailTaken;
  if (error instanceof InvalidCredentialsError) return AccountFailureKey.InvalidCredentials;
  if (error instanceof InvalidRecoveryCodeError) return AccountFailureKey.InvalidRecoveryCode;
  if (error instanceof RegistrationClosedError) return AccountFailureKey.RegistrationClosed;
  if (error instanceof RegistrationNotConfirmedError) return AccountFailureKey.RegistrationNotConfirmed;
  if (error instanceof KdfParamsRejectedError) return AccountFailureKey.KdfRejected;
  if (error instanceof PassphraseMismatchError) return AccountFailureKey.PassphraseMismatch;
  if (error instanceof SpaceTakenError) return AccountFailureKey.SpaceTaken;
  if (error instanceof PassphraseSpacesDisabledError) return AccountFailureKey.PassphraseSpacesDisabled;
  if (error instanceof AccountsUnsupportedError) return AccountFailureKey.AccountsUnsupported;
  if (error instanceof SessionRejectedError) return AccountFailureKey.SessionRejected;
  return syncFailureKey(error);
}

/** Like `accountFailureKey`, for requests that send a mail: a rate limit there means "too many mails to this address". */
export function mailFailureKey(error: unknown): AccountFailureKey | SyncFailureKey {
  if (error instanceof SyncHttpError && error.status === 429) return AccountFailureKey.TooManyMails;
  return accountFailureKey(error);
}

/** Whether trying the same request again can succeed (no connection, server busy or failing). */
export function isRetryableFailure(key: AccountFailureKey | SyncFailureKey): boolean {
  return (
    key === SyncFailureKey.Network ||
    key === SyncFailureKey.ServerUnavailable ||
    key === SyncFailureKey.Server ||
    key === SyncFailureKey.RateLimited
  );
}
